/**
 * The device's orientation, steadied and turned to true north (Sprint 103.0,
 * 103.1).
 *
 * Every phone browser gives two readings. A relative one, from the gyroscope:
 * smooth and immediate, but from wherever the page started. And north, from the
 * compass: noisy and easily disturbed.
 * - Safari on iOS fires `deviceorientation` with the relative angles and adds
 *   `webkitCompassHeading`, clockwise from magnetic north, only after the page
 *   asked with `requestPermission` from a tap.
 * - Chrome on Android fires `deviceorientation`, relative, and
 *   `deviceorientationabsolute`, measured from magnetic north.
 *
 * Both earth frames share the vertical, so they differ by one turn about it:
 * the reading shown is the relative one, turned by the offset `NorthEstimate`
 * averages from the compass, and by the place's magnetic declination
 * (ADR-0120 Rule 6).
 */

import { angleDifference, NorthEstimate } from '../model/heading.ts';
import type { DeviceAngles } from '../model/pose.ts';

export interface OrientationReading extends DeviceAngles {
  /** The heading's likely error in degrees, once known: the phone's own estimate or the compass's scatter. */
  readonly errorDegrees?: number;
  /** For the debug readout: the compass alone, clockwise from true north. */
  readonly rawHeading?: number;
}

type PermissionRequest = () => Promise<'granted' | 'denied'>;

interface IOSOrientationEvent extends DeviceOrientationEvent {
  readonly webkitCompassHeading?: number | null;
  readonly webkitCompassAccuracy?: number;
}

function permissionRequest(): PermissionRequest | undefined {
  const constructor = globalThis.DeviceOrientationEvent as unknown as
    { requestPermission?: PermissionRequest } | undefined;
  return typeof constructor?.requestPermission === 'function'
    ? constructor.requestPermission.bind(constructor)
    : undefined;
}

/** Whether this browser asks before giving the orientation (Safari on iOS 13 and later). */
export function motionNeedsPermission(): boolean {
  return permissionRequest() !== undefined;
}

/** Must run inside a tap's handler: Safari refuses the request otherwise. */
export async function requestMotion(): Promise<boolean> {
  const request = permissionRequest();
  if (request === undefined) return true;
  try {
    return (await request()) === 'granted';
  } catch {
    return false;
  }
}

/** A relative reading older than this is not paired with an absolute one. */
const PAIRING_MS = 200;

/** The heading, clockwise from north, of an alpha (which counts counter-clockwise). */
const headingOf = (alpha: number): number => (360 - alpha) % 360;

/**
 * Calls `onReading` with each steadied, true-north reading, and `onSilent` once
 * if none has arrived after `waitMs` (a computer, a refused permission, a
 * browser with no compass). `declination` is read at each reading, so a change
 * of place applies at once. Returns the unsubscribe.
 */
export function watchOrientation(
  onReading: (reading: OrientationReading) => void,
  onSilent: () => void,
  declination: () => number,
  waitMs = 2000
): () => void {
  let heard = false;
  const north = new NorthEstimate();
  let iosError: number | undefined;
  let relative: { alpha: number; at: number } | undefined;
  let lastRaw: number | undefined;

  const emit = (event: DeviceOrientationEvent, alpha: number, now: number): void => {
    const offset = north.offset;
    if (offset === undefined || event.beta === null || event.gamma === null) return;
    heard = true;
    const settled = north.settled(now);
    const scatter = settled ? north.scatterDegrees : undefined;
    const error = iosError === undefined ? scatter : Math.max(iosError, scatter ?? 0);
    onReading({
      // True heading = magnetic + declination, and alpha turns the other way.
      alpha: (alpha + offset - declination() + 720) % 360,
      beta: event.beta,
      gamma: event.gamma,
      ...(error === undefined ? {} : { errorDegrees: error }),
      ...(lastRaw === undefined ? {} : { rawHeading: (lastRaw + declination() + 360) % 360 })
    });
  };

  const onRelative = (event: Event): void => {
    const e = event as IOSOrientationEvent;
    if (e.alpha === null) return;
    const now = performance.now();
    if (e.webkitCompassHeading !== undefined && e.webkitCompassHeading !== null) {
      // iOS: one event carries both. A negative accuracy means no heading at all.
      const accuracy = e.webkitCompassAccuracy;
      if (accuracy === undefined || accuracy >= 0) {
        iosError = accuracy;
        lastRaw = e.webkitCompassHeading;
        north.update(angleDifference(headingOf(e.webkitCompassHeading), e.alpha), now);
      } else {
        iosError = Number.POSITIVE_INFINITY;
      }
      emit(e, e.alpha, now);
      return;
    }
    if (e.absolute) {
      // A browser whose only event is absolute: it is its own compass.
      onAbsolute(event);
      return;
    }
    relative = { alpha: e.alpha, at: now };
    emit(e, e.alpha, now);
  };

  const onAbsolute = (event: Event): void => {
    const e = event as DeviceOrientationEvent;
    if (e.alpha === null) return;
    const now = performance.now();
    lastRaw = headingOf(e.alpha);
    if (relative !== undefined && now - relative.at <= PAIRING_MS) {
      north.update(angleDifference(e.alpha, relative.alpha), now);
    } else if (relative === undefined) {
      // No gyroscope reading at all: the absolute one is shown as it comes.
      north.update(0, now);
      emit(e, e.alpha, now);
    }
  };

  globalThis.addEventListener('deviceorientation', onRelative);
  globalThis.addEventListener('deviceorientationabsolute', onAbsolute);
  const timer = globalThis.setTimeout(() => {
    if (!heard) onSilent();
  }, waitMs);
  return () => {
    globalThis.clearTimeout(timer);
    globalThis.removeEventListener('deviceorientation', onRelative);
    globalThis.removeEventListener('deviceorientationabsolute', onAbsolute);
  };
}

/** `screen.orientation.angle`, with the older `window.orientation` as a fallback. */
export function screenAngle(): number {
  const angle = globalThis.screen?.orientation?.angle;
  if (typeof angle === 'number') return angle;
  const legacy = (globalThis as { orientation?: number }).orientation;
  return typeof legacy === 'number' ? (legacy + 360) % 360 : 0;
}
